import { PRIORITY_LEVEL_LABELS_FR, REPORT_STATUS_LABELS_FR, SOURCE_CHANNEL_LABELS_FR, } from '@ci-ville/shared';
import { todayIso } from './dates';
export const EMPTY_FILTERS = { search: '', statuses: [], priorities: [], categories: [], types: [], channels: [], period: 'all' };
export const PERIODS = [
    { id: 'all', label: 'Tout' },
    { id: '7d', label: '7 jours' },
    { id: '30d', label: '30 jours' },
    { id: '90d', label: '90 jours' },
    { id: 'month', label: 'Ce mois-ci' },
    { id: 'year', label: 'Cette année' },
    { id: 'custom', label: 'Dates…' },
];
const OPEN = ['OPEN', 'IN_PROGRESS'];
export const QUICK_VIEWS = [
    { id: 'todo', label: 'À traiter', title: 'Nouveaux et en cours' },
    { id: 'late', label: 'En retard', title: 'Ouverts dont le délai cible est dépassé' },
    { id: 'priority', label: 'Prioritaires', title: 'Ouverts de priorité urgente ou haute' },
    { id: 'unassigned', label: 'Non assignés', title: 'Ouverts sans responsable', needsAssign: true },
    { id: 'mine', label: 'Mes dossiers', title: 'Ouverts qui me sont assignés', fieldAgentFirst: true },
    { id: 'all', label: 'Tous', title: 'Aucun filtre' },
];
/** Une vue rapide remplace les critères de tri des dossiers, mais garde la recherche, la période et le quartier. */
export function applyQuickView(f, id, userId) {
    const base = { ...EMPTY_FILTERS, search: f.search, period: f.period, fromDate: f.fromDate, toDate: f.toDate, zoneId: f.zoneId, view: id };
    switch (id) {
        case 'todo': return { ...base, statuses: OPEN };
        case 'late': return { ...base, statuses: OPEN, late: true };
        case 'priority': return { ...base, statuses: OPEN, priorities: ['URGENT', 'HIGH'] };
        case 'unassigned': return { ...base, statuses: OPEN, unassigned: true };
        case 'mine': return { ...base, statuses: OPEN, assignedToUserId: userId };
        default: return { ...base, view: undefined };
    }
}
// ── Période → dates ─────────────────────────────────────────────────
function isoDaysAgo(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function periodDates(f) {
    const today = todayIso();
    switch (f.period) {
        case '7d': return { fromDate: isoDaysAgo(6) };
        case '30d': return { fromDate: isoDaysAgo(29) };
        case '90d': return { fromDate: isoDaysAgo(89) };
        case 'month': return { fromDate: `${today.slice(0, 7)}-01` };
        case 'year': return { fromDate: `${today.slice(0, 4)}-01-01` };
        case 'custom': return { fromDate: f.fromDate, toDate: f.toDate };
        default: return {};
    }
}
export function toApiParams(f, tenantId) {
    return {
        tenantId,
        search: f.search.trim() || undefined,
        status: f.statuses,
        priority: f.priorities,
        categoryId: f.categories,
        subcategoryId: f.types,
        sourceChannel: f.channels,
        assignedToUserId: f.assignedToUserId,
        zoneId: f.zoneId,
        unassigned: f.unassigned || undefined,
        late: f.late || undefined,
        ...periodDates(f),
    };
}
// ── URL ─────────────────────────────────────────────────────────────
const list = (v) => (v ? v.split(',').filter(Boolean) : []);
export function filtersFromSearch(sp) {
    return {
        view: sp.get('vue') || undefined,
        search: sp.get('q') ?? '',
        statuses: list(sp.get('statut')),
        priorities: list(sp.get('priorite')),
        categories: list(sp.get('categorie')),
        types: list(sp.get('type')),
        channels: list(sp.get('canal')),
        assignedToUserId: sp.get('assigne') ?? undefined,
        zoneId: sp.get('quartier') ?? undefined,
        unassigned: sp.get('nonassigne') === '1' || undefined,
        late: sp.get('retard') === '1' || undefined,
        period: sp.get('periode') || 'all',
        fromDate: sp.get('du') ?? undefined,
        toDate: sp.get('au') ?? undefined,
    };
}
/** Écrit les filtres dans l'URL en conservant les autres paramètres (onglet, signalement ouvert…). */
export function writeFilters(sp, f) {
    const out = new URLSearchParams(sp);
    const set = (k, v) => (v ? out.set(k, v) : out.delete(k));
    set('vue', f.view);
    set('q', f.search.trim() || undefined);
    set('statut', f.statuses.join(','));
    set('priorite', f.priorities.join(','));
    set('categorie', f.categories.join(','));
    set('type', f.types.join(','));
    set('canal', f.channels.join(','));
    set('assigne', f.assignedToUserId);
    set('quartier', f.zoneId);
    set('nonassigne', f.unassigned && '1');
    set('retard', f.late && '1');
    set('periode', f.period !== 'all' && f.period);
    set('du', f.period === 'custom' && f.fromDate);
    set('au', f.period === 'custom' && f.toDate);
    return out;
}
/** Paramètres de filtre seulement, pour construire un lien vers une autre page. */
export function filtersQuery(f) {
    const s = writeFilters(new URLSearchParams(), f).toString();
    return s ? `?${s}` : '';
}
export function activeChips(f, lookups) {
    const chips = [];
    const drop = (patch) => (x) => ({ ...x, ...patch, view: undefined });
    for (const s of f.statuses)
        chips.push({ key: `s-${s}`, label: REPORT_STATUS_LABELS_FR[s], remove: (x) => ({ ...x, view: undefined, statuses: x.statuses.filter((v) => v !== s) }) });
    for (const p of f.priorities)
        chips.push({ key: `p-${p}`, label: `Priorité ${PRIORITY_LEVEL_LABELS_FR[p].toLowerCase()}`, remove: (x) => ({ ...x, view: undefined, priorities: x.priorities.filter((v) => v !== p) }) });
    for (const c of f.categories) {
        const label = lookups.categories?.find((x) => x.id === c)?.nameFr ?? 'Catégorie';
        chips.push({ key: `cat-${c}`, label, remove: (x) => ({ ...x, view: undefined, categories: x.categories.filter((v) => v !== c) }) });
    }
    for (const t of f.types) {
        const label = lookups.categories?.flatMap((x) => x.subcategories ?? []).find((x) => x.id === t)?.nameFr ?? 'Type de problème';
        chips.push({ key: `type-${t}`, label, remove: (x) => ({ ...x, view: undefined, types: x.types.filter((v) => v !== t) }) });
    }
    for (const c of f.channels)
        chips.push({ key: `c-${c}`, label: SOURCE_CHANNEL_LABELS_FR[c] ?? c, remove: (x) => ({ ...x, view: undefined, channels: x.channels.filter((v) => v !== c) }) });
    if (f.assignedToUserId) {
        const p = lookups.people?.find((x) => x.id === f.assignedToUserId);
        const label = f.assignedToUserId === lookups.meId ? 'Assignés à moi' : p ? `Assignés à ${p.firstName} ${p.lastName}` : 'Assignés à une personne';
        chips.push({ key: 'as', label, remove: drop({ assignedToUserId: undefined }) });
    }
    if (f.zoneId) {
        const label = f.zoneId === 'none' ? 'Hors des quartiers' : lookups.zones?.find((z) => z.id === f.zoneId)?.name ?? 'Quartier';
        chips.push({ key: 'zone', label, remove: drop({ zoneId: undefined }) });
    }
    if (f.unassigned)
        chips.push({ key: 'un', label: 'Non assignés', remove: drop({ unassigned: undefined }) });
    if (f.late)
        chips.push({ key: 'late', label: 'En retard', remove: drop({ late: undefined }) });
    if (f.period !== 'all') {
        const label = f.period === 'custom'
            ? `${f.fromDate ? `du ${f.fromDate}` : ''}${f.toDate ? ` au ${f.toDate}` : ''}`.trim() || 'Dates'
            : PERIODS.find((p) => p.id === f.period).label;
        chips.push({ key: 'per', label, remove: (x) => ({ ...x, period: 'all', fromDate: undefined, toDate: undefined }) });
    }
    return chips;
}
/** Nombre de critères actifs (hors recherche) */
export function activeCount(f) {
    return activeChips(f, {}).length;
}
//# sourceMappingURL=report-filters.js.map